<script lang="ts">
  import { onMount, type Snippet } from 'svelte'
  import {
    computePosition,
    flip,
    shift,
    arrow,
    offset,
    type Placement,
  } from '@floating-ui/dom'

  type Props = {
    children: Snippet
    title: string
    placement?: Placement
  }

  const { children, title, placement = 'bottom' }: Props = $props()

  let tooltipElement: HTMLDivElement
  let contentElement: HTMLDivElement
  let arrowElement: HTMLDivElement

  onMount(() => {
    const childElement = contentElement.querySelector('*')

    childElement?.addEventListener('focus', showTooltip)
    childElement?.addEventListener('blur', hideTooltip)
  })

  const update = () => {
    computePosition(contentElement, tooltipElement, {
      placement,
      middleware: [
        offset(3),
        flip(),
        shift({ padding: 8 }),
        arrow({ element: arrowElement, padding: 4 }),
      ],
    }).then(({ x, y, placement, middlewareData }) => {
      Object.assign(tooltipElement.style, {
        left: `${x}px`,
        top: `${y}px`,
      })

      if (middlewareData.arrow) {
        const { x: arrowX, y: arrowY } = middlewareData.arrow

        const arrowPosition = {
          top: 'bottom',
          right: 'left',
          bottom: 'top',
          left: 'right',
        }

        const staticSide: (typeof arrowPosition)[keyof typeof arrowPosition]
          = arrowPosition[placement.split('-')[0] as keyof typeof arrowPosition]

        Object.assign(arrowElement.style, {
          left: arrowX != null ? `${arrowX}px` : '',
          top: arrowY != null ? `${arrowY}px` : '',
          right: '',
          bottom: '',
          [staticSide]: '-2px',
        })
      }
    })
  }

  const showTooltip = () => {
    tooltipElement.style.display = 'block'
    update()
  }

  const hideTooltip = () => {
    tooltipElement.style.display = ''
  }
</script>

<div class="tooltip-container">
  <div
    bind:this={contentElement}
    class="tooltip-content"
    onblur={hideTooltip}
    onfocus={showTooltip}
    onmouseenter={showTooltip}
    onmouseleave={hideTooltip}
    role="none"
  >
    {@render children()}
  </div>
  <div bind:this={tooltipElement} class="tooltip" role="tooltip">
    {title}
    <div bind:this={arrowElement} class="arrow"></div>
  </div>
</div>

<style lang="scss">
  .tooltip-container {
    position: relative;

    .tooltip {
      z-index: 9999;
      display: none;
      background-color: var(--tooltip-background);
      color: var(--tooltip-foreground);
      padding: 0.375rem 0.5rem;
      border-radius: 0.25rem;
      white-space: nowrap;
      position: absolute;
      line-height: 120%;
      font-weight: 600;
      font-size: 0.625rem;
      top: 0;
      left: 0;
      pointer-events: none;
      user-select: none;

      .arrow {
        position: absolute;
        background: var(--tooltip-background);
        width: 0.25rem;
        height: 0.25rem;
        transform: rotate(45deg);
      }
    }
  }
</style>
